<script setup>
import { Button } from '@dashhudson/dashing-ui/button';
import { useElementSize } from '@vueuse/core';
import { debounce } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import CommentDetailHeader from '@/app/community/components/CommentDetailHeader.vue';
import CommentItem from '@/app/community/components/CommentItem.vue';
import DetailBase from '@/app/community/components/DetailBase.vue';
import {
  COMMUNITY_INTERACTION_PLATFORM_BY_TYPE,
  COMMUNITY_INTERACTION_TYPES,
} from '@/app/community/constants';
import { formatCount } from '@/app/community/utils/comment-detail';
import { getUrlPathByPlatform } from '@/app/community/utils/comments';
import { communityCommentPopupEvent } from '@/app/community/utils/mixpanel-events';
import { useCommunityCommentsStore } from '@/stores/community-comments';
import { useCommunityInteractionStore } from '@/stores/community-interactions';
import { useCommunityTranslationStore } from '@/stores/community-translation';
import { useIdentityStore } from '@/stores/identity';

const commentsStore = useCommunityCommentsStore();
const interactionStore = useCommunityInteractionStore();
const identityStore = useIdentityStore();
const translationStore = useCommunityTranslationStore();

const route = useRoute();
const router = useRouter();

const scrollContainer = ref(null);

const media = computed(() => interactionStore.selectedCommunityInteractionMedia);
const commentDetail = computed(() => commentsStore.commentDetail);
const brand = computed(() => identityStore.identityBrandsById[commentDetail.value?.brandId] ?? {});
const activeItem = computed(() => interactionStore.selectedCommunityInteraction);
const isUgc = computed(
  () =>
    [
      COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET,
      COMMUNITY_INTERACTION_TYPES.TWITTER_QUOTE,
    ].includes(commentDetail.value.type) ||
    (commentDetail.value.type === COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION &&
      !commentDetail.value.mediaId),
);
const parentId = computed(
  () => commentDetail.value?.parentId ?? commentDetail.value?.twitterParentTweetId,
);
const hasUgcParent = computed(() => {
  const { topParentStatus } = commentDetail.value ?? {};
  return !!topParentStatus && !topParentStatus.isOwned && !!parentId.value;
});
const isTopUgcComment = computed(
  () =>
    /* 1) Retweets and Quote tweets
     * 2) Root level UGC mentions without media
     * 3) Tweets replying to UGC parent
     */
    isUgc.value &&
    (hasUgcParent.value ||
      !parentId.value ||
      !!commentDetail.value?.quotedStatus ||
      (!!parentId.value && !commentDetail.value?.topParentStatus)),
);
const comment = computed(() => {
  const { topParentStatus, ...commentItem } = commentDetail.value ?? {};
  if (hasUgcParent.value) {
    return { ...topParentStatus, replies: [commentItem] };
  }
  return commentDetail.value;
});
function containsActiveItem(replies, activeItemId) {
  return (
    !!activeItemId &&
    !!replies &&
    replies?.some(
      (reply) =>
        reply?.id === activeItemId ||
        (reply?.replies && containsActiveItem(reply.replies, activeItemId)),
    )
  );
}
const threadHasActiveItem = computed(
  () =>
    activeItem.value?.type !== COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION ||
    comment.value?.id === activeItem.value?.id ||
    containsActiveItem(comment.value?.replies, activeItem.value?.id),
);

const showCommentPopupButton = computed(
  () =>
    !isTopUgcComment.value &&
    comment.value?.type !== COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION &&
    !route.meta?.isSalesforceEmbed,
);
const commentCount = computed(() => comment.value?.commentCount);
const commentPopupButtonLabel = computed(() => {
  const isMention = activeItem.value.type === COMMUNITY_INTERACTION_TYPES.TWITTER_MENTION;
  const singularSuffix = isMention ? 'Reply' : 'Comment';
  const pluralSuffix = isMention ? 'Replies' : 'Comments';
  const formattedCount = formatCount(commentCount.value, singularSuffix, pluralSuffix);
  const action = commentCount.value > 1 ? 'See All' : 'See';
  return commentCount.value > 0 ? `${action} ${formattedCount}` : `No ${pluralSuffix}`;
});

async function openCommentPopup() {
  const { platform, platformValue, messageType } =
    COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[activeItem.value.type];

  const name = `${route.name}.${getUrlPathByPlatform(platformValue)}`;
  if (router.history?.pending?.name === name) return;
  await router.push({
    name,
    params: {
      brandLabel: identityStore.currentBrand.label,
      interactionId: String(activeItem.value.id),
    },
  });
  // Send mixpanel event
  communityCommentPopupEvent({
    details: { platform, messageType },
    communityInteraction: activeItem.value,
  });
}

async function onSeeCommentsClicked() {
  // Mark the comment as read if unread
  if (!activeItem.value.isRead) {
    interactionStore.updateCommunityInteraction(activeItem.value.id, { isRead: true });
  }

  const { platformValue } = COMMUNITY_INTERACTION_PLATFORM_BY_TYPE[activeItem.value?.type];
  if (platformValue) {
    await commentsStore.getCommentPopupDetail({
      id: activeItem.value.id,
      platform: platformValue,
    });
    await commentsStore.setCommentMedia(media.value);
    const { id, text } = commentsStore.commentPopupDetail;
    const simplifiedReplies = commentsStore.commentPopupDetail.replies.map((r) => ({
      id: r.id,
      text: r.text,
    }));
    await openCommentPopup();
    await translationStore.detectLanguages([...simplifiedReplies, { id, text }]);
  }
}

function onNewReply() {
  const c = scrollContainer.value;
  if (c) {
    c.scrollTop = c.scrollHeight;
  }
}

const baseRef = ref(null);

const { height: baseRefHeight } = useElementSize(baseRef);

const headerMaxHeight = ref(0);

function updateHeaderMax() {
  headerMaxHeight.value = Math.floor((baseRefHeight.value * 2) / 5);
}

const debouncedUpdate = debounce(updateHeaderMax, 200);

watch(
  () => baseRefHeight.value,
  () => {
    debouncedUpdate();
  },
  { immediate: true },
);

watch(
  () => comment.value?.id,
  async (to) => {
    if (to) {
      const { id, text, replies } = commentDetail.value ?? {};
      const comments = [...replies.map((r) => ({ id: r.id, text: r.text })), { id, text }];
      await translationStore.detectLanguages(comments);
    }
  },
  { immediate: true },
);
</script>

<template>
  <DetailBase v-if="commentDetail && activeItem" ref="baseRef">
    <template #header>
      <CommentDetailHeader
        v-if="!isTopUgcComment"
        :media="media"
        :comment="comment"
        :active-comment="activeItem"
        :ugc-tweet="isUgc"
        :brand="brand"
        :clamp-height="headerMaxHeight"
        data-test="CommentDetailHeader"
      />
    </template>
    <template #content>
      <div class="dash-flex-1 dash-min-h-0">
        <div
          ref="scrollContainer"
          class="dash-h-full dash-overflow-x-hidden dash-overflow-y-scroll !space-y-6 !pb-24"
        >
          <CommentItem
            :media="media"
            :comment="comment"
            :active-item="activeItem"
            :ugc="isTopUgcComment"
            :brand="brand"
            :has-active-item="threadHasActiveItem"
            root
            @scroll-to-new-reply="onNewReply"
          />
          <div class="dash-flex dash-justify-center">
            <Button
              v-if="showCommentPopupButton"
              :label="commentPopupButtonLabel"
              class="!mb-6"
              severity="tertiary"
              data-test="CommentPopupButton"
              @click="onSeeCommentsClicked"
            />
          </div>
        </div>
      </div>
    </template>
  </DetailBase>
</template>
